import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, LoadingSkeleton } from '@nearrme/ui-web';
import { listPermissions, listRoles, updateRolePermissions } from '../api';

export function AdminRolesPage() {
  const queryClient = useQueryClient();
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: listRoles });
  const permissions = useQuery({ queryKey: ['admin', 'permissions'], queryFn: listPermissions });
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>('');
  const [draftPermissions, setDraftPermissions] = useState<Record<string, string[]>>({});

  const selectedRole = useMemo(() => {
    const allRoles = roles.data ?? [];
    return allRoles.find((role) => role.roleCode === (selectedRoleCode || allRoles[0]?.roleCode));
  }, [roles.data, selectedRoleCode]);

  const selectedCodes = selectedRole
    ? draftPermissions[selectedRole.roleCode] ?? selectedRole.permissionCodes ?? []
    : [];

  const groupedPermissions = useMemo(() => {
    return (permissions.data ?? []).reduce<Record<string, typeof permissions.data>>((groups, permission) => {
      const category = permission.category || 'Other';
      groups[category] = groups[category] ?? [];
      groups[category]!.push(permission);
      return groups;
    }, {});
  }, [permissions.data]);

  const mutation = useMutation({
    mutationFn: ({ roleCode, permissionCodes }: { roleCode: string; permissionCodes: string[] }) =>
      updateRolePermissions(roleCode, permissionCodes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });

  const togglePermission = (permissionCode: string, checked: boolean) => {
    if (!selectedRole) return;
    const next = new Set(selectedCodes);
    if (checked) next.add(permissionCode);
    else next.delete(permissionCode);
    setDraftPermissions((current) => ({ ...current, [selectedRole.roleCode]: [...next] }));
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Roles and permissions</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Nearrme Admin controls the role templates available to Nearrme, company, store, and consumer profiles.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <Card padded>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Roles</h2>
          {roles.isLoading ? (
            <div className="mt-4 space-y-2">
              {[1, 2, 3].map((item) => <LoadingSkeleton key={item} className="h-14 w-full" />)}
            </div>
          ) : (
            <div className="mt-4 grid gap-2">
              {(roles.data ?? []).map((role) => {
                const active = (selectedRole?.roleCode ?? roles.data?.[0]?.roleCode) === role.roleCode;
                return (
                  <button
                    key={role.roleCode}
                    type="button"
                    onClick={() => setSelectedRoleCode(role.roleCode)}
                    className={`rounded-lg border px-3 py-3 text-left transition ${
                      active
                        ? 'border-slate-950 bg-slate-950 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950'
                        : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900'
                    }`}
                  >
                    <div className="font-semibold">{role.displayName}</div>
                    <div className={`mt-1 text-xs ${active ? 'text-slate-200 dark:text-slate-700' : 'text-slate-500 dark:text-slate-400'}`}>
                      {role.profileType} | {role.scopeLevel}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <Card padded>
          {selectedRole ? (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-950 dark:text-slate-50">{selectedRole.displayName}</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{selectedRole.profileSummary}</p>
                </div>
                <Button
                  variant="primary"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ roleCode: selectedRole.roleCode, permissionCodes: selectedCodes })}
                >
                  Save permissions
                </Button>
              </div>

              {permissions.isLoading ? (
                <div className="mt-5 space-y-2">
                  {[1, 2, 3, 4].map((item) => <LoadingSkeleton key={item} className="h-14 w-full" />)}
                </div>
              ) : (
                <div className="mt-5 grid gap-4">
                  {Object.entries(groupedPermissions).map(([category, items]) => (
                    <div key={category}>
                      <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{category}</h3>
                      <div className="mt-2 grid gap-2 md:grid-cols-2">
                        {(items ?? []).map((permission) => (
                          <label
                            key={permission.permissionCode}
                            className="flex gap-3 rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-800"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCodes.includes(permission.permissionCode)}
                              onChange={(e) => togglePermission(permission.permissionCode, e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            />
                            <span>
                              <span className="block font-semibold text-slate-900 dark:text-slate-100">{permission.displayName}</span>
                              <span className="mt-1 block text-xs leading-5 text-slate-600 dark:text-slate-400">{permission.description}</span>
                              <span className="mt-1 block font-mono text-[11px] text-slate-400">{permission.permissionCode}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {mutation.error && (
                <div className="mt-4 text-sm text-red-600 dark:text-red-400">{(mutation.error as Error).message}</div>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-500 dark:text-slate-400">Select a role to manage permissions.</div>
          )}
        </Card>
      </div>
    </div>
  );
}

